import type { InsightCtaId } from "@/lib/content/insights";

export const INSIGHT_SCROLL_EVENT_NAME = "insight_scroll_milestone_reached";
export const INSIGHT_SCROLL_MILESTONES = [25, 50, 75, 90] as const;

export type InsightCtaPlacement =
  | "desktop_sticky_rail"
  | "mobile_inline_contextual"
  | "mobile_article_end";

type InsightCtaEventInput = {
  ctaKind: InsightCtaId;
  insightSlug: string;
  placement: InsightCtaPlacement;
};

type InsightScrollMeasurement = {
  articleTop: number;
  articleHeight: number;
  viewportBottom: number;
  trigger: "initial" | "resize" | "scroll";
};

type InsightEventProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

type InsightScrollMilestoneSessionInput = {
  insightSlug: string;
  capture: (eventName: string, properties: InsightEventProperties) => void;
};

const insightCtaContract = {
  guidedRollout: {
    eventName: "guided_rollout_clicked",
    ctaId: "insight_guided_rollout",
  },
  requestDemo: {
    eventName: "demo_cta_clicked",
    ctaId: "insight_request_demo",
  },
} as const satisfies Record<
  InsightCtaId,
  { eventName: string; ctaId: string }
>;

export function buildInsightCtaEvent({
  ctaKind,
  insightSlug,
  placement,
}: InsightCtaEventInput) {
  const contract = insightCtaContract[ctaKind];

  return {
    eventName: contract.eventName,
    eventProperties: {
      analytics_page_path: `/insights/${insightSlug}`,
      app_area: "insights",
      insight_slug: insightSlug,
      cta_id: contract.ctaId,
      placement,
      route_name: "insights.detail_cta",
    },
    analyticsAudience: "public_only" as const,
  };
}

export function calculateInsightArticleProgress({
  articleTop,
  articleHeight,
  viewportBottom,
}: Omit<InsightScrollMeasurement, "trigger">) {
  if (!Number.isFinite(articleHeight) || articleHeight <= 0) {
    return 0;
  }

  const exposedHeight = viewportBottom - articleTop;
  const progress = (exposedHeight / articleHeight) * 100;

  return Math.min(100, Math.max(0, progress));
}

export function createInsightScrollMilestoneSession({
  insightSlug,
  capture,
}: InsightScrollMilestoneSessionInput) {
  const reachedMilestones = new Set<number>();
  let hasObservedScroll = false;

  return {
    record(measurement: InsightScrollMeasurement) {
      if (measurement.trigger === "initial") {
        return [] as number[];
      }

      if (measurement.trigger === "scroll") {
        hasObservedScroll = true;
      }

      if (!hasObservedScroll) {
        return [] as number[];
      }

      const progress = calculateInsightArticleProgress(measurement);
      const newlyReached = INSIGHT_SCROLL_MILESTONES.filter(
        (milestone) => progress >= milestone && !reachedMilestones.has(milestone),
      );

      for (const milestone of newlyReached) {
        reachedMilestones.add(milestone);
        capture(INSIGHT_SCROLL_EVENT_NAME, {
          analytics_page_path: `/insights/${insightSlug}`,
          app_area: "insights",
          insight_slug: insightSlug,
          milestone,
          route_name: "insights.detail_scroll",
        });
      }

      return newlyReached;
    },
  };
}
